import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { authAPI } from "@/services/api";
import { applyAuthSession } from "@/utils/authSession";
import FirebasePhoneAuth from "@/components/auth/FirebasePhoneAuth";
import AuthShell from "@/components/auth/AuthShell";
import { isFirebaseConfigured } from "@/lib/firebase";
import { Phone, KeyRound, Loader2 } from "lucide-react";

const BRMLogin = ({ onLogin }) => {
  const [searchParams] = useSearchParams();
  const [authConfig, setAuthConfig] = useState({ enabled: false, use_legacy_auth: true });
  const [loginMethod, setLoginMethod] = useState("phone");
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState("");

  const firebaseOn = isFirebaseConfigured() && authConfig.enabled;
  const showLegacy = authConfig.use_legacy_auth;

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) setReferralCode(ref);
    authAPI.getConfig().then(setAuthConfig).catch(() => {});
  }, [searchParams]);

  const handleSession = (session) => {
    applyAuthSession(session, onLogin);
  };

  const formatPhone = (value) => value.replace(/\D/g, "");

  const getFullPhone = () => {
    const cleanPhone = formatPhone(phone);
    if (cleanPhone.startsWith("0")) return `+234${cleanPhone.slice(1)}`;
    if (cleanPhone.startsWith("234")) return `+${cleanPhone}`;
    return `+234${cleanPhone}`;
  };

  const handleRequestOTP = async () => {
    if (!phone || phone.length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }
    setLoading(true);
    try {
      const response = await authAPI.requestOTP(getFullPhone(), referralCode || null);
      toast.success(`OTP sent! (Mock: ${response.data.mock_otp})`);
      setStep("otp");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      toast.error("Please enter the 6-digit OTP");
      return;
    }
    setLoading(true);
    try {
      const response = await authAPI.verifyOTP(getFullPhone(), otp, referralCode || null);
      toast.success("Login successful!");
      applyAuthSession(response.data, onLogin);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const handlePINLogin = async () => {
    if (!phone || phone.length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }
    if (pin.length !== 4) {
      toast.error("Please enter your 4-digit PIN");
      return;
    }
    setLoading(true);
    try {
      const response = await authAPI.loginWithPIN(getFullPhone(), pin);
      toast.success("Login successful!");
      applyAuthSession({ ...response.data, requires_pin_setup: false }, onLogin);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      eyebrow="BRM workspace"
      title="Sign In"
      description="Sign in with phone verification, OTP, or your BRM PIN."
      panelTitle="BRM field workspace"
      panelDescription="Enroll users, share campaign links, track wallet earnings, and manage referrals from a mobile-first Proovam dashboard."
      backLabel="Back to dashboard"
    >
      <div className="space-y-7">
        {firebaseOn && (
          <div className="space-y-6">
            <FirebasePhoneAuth role="brm" referralCode={referralCode} onSession={handleSession} />
            {referralCode && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
                Referral code applied: <span className="font-medium">{referralCode}</span>
              </div>
            )}
          </div>
        )}

        {showLegacy && (
          <>
            {firebaseOn && (
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">Dev legacy auth</span>
                </div>
              </div>
            )}

            {step === "phone" ? (
              <Tabs value={loginMethod} onValueChange={setLoginMethod} className="w-full">
                <TabsList className="mb-6 grid h-11 w-full grid-cols-2 rounded-lg bg-gray-100 p-1">
                  <TabsTrigger value="otp" className="rounded-md" data-testid="tab-otp">
                    <Phone className="mr-2 h-4 w-4" />
                    Mock OTP
                  </TabsTrigger>
                  <TabsTrigger value="pin" className="rounded-md" data-testid="tab-pin">
                    <KeyRound className="mr-2 h-4 w-4" />
                    PIN
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="otp" className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="phone-otp" className="text-sm font-medium text-gray-700">
                      Phone Number <span className="text-[#f04438]">*</span>
                    </Label>
                    <div className="phone-input-group border border-gray-200 shadow-theme-xs">
                      <span className="phone-input-prefix">+234</span>
                      <input
                        id="phone-otp"
                        type="tel"
                        placeholder="8012345678"
                        value={phone}
                        onChange={(e) => setPhone(formatPhone(e.target.value))}
                        className="phone-input-field h-11 text-sm"
                        maxLength={11}
                      />
                    </div>
                  </div>
                  <Button className="h-11 w-full rounded-lg font-semibold" onClick={handleRequestOTP} disabled={loading}>
                    {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Send OTP
                  </Button>
                </TabsContent>

                <TabsContent value="pin" className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="phone-pin" className="text-sm font-medium text-gray-700">
                      Phone Number <span className="text-[#f04438]">*</span>
                    </Label>
                    <div className="phone-input-group border border-gray-200 shadow-theme-xs">
                      <span className="phone-input-prefix">+234</span>
                      <input
                        id="phone-pin"
                        type="tel"
                        placeholder="8012345678"
                        value={phone}
                        onChange={(e) => setPhone(formatPhone(e.target.value))}
                        className="phone-input-field h-11 text-sm"
                        maxLength={11}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pin" className="text-sm font-medium text-gray-700">
                      4-Digit PIN <span className="text-[#f04438]">*</span>
                    </Label>
                    <Input
                      id="pin"
                      type="password"
                      value={pin}
                      onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                      maxLength={4}
                      className="h-11 rounded-lg border-gray-200 text-center text-xl tracking-widest shadow-theme-xs focus:border-brand-400"
                    />
                  </div>
                  <Button className="h-11 w-full rounded-lg font-semibold" onClick={handlePINLogin} disabled={loading}>
                    Login with PIN
                  </Button>
                </TabsContent>
              </Tabs>
            ) : (
              <div className="space-y-6">
                <p className="text-center text-sm text-gray-500">OTP sent to {getFullPhone()}</p>
                <div className="flex justify-center">
                  <InputOTP value={otp} onChange={setOtp} maxLength={6}>
                    <InputOTPGroup>
                      {[0, 1, 2, 3, 4, 5].map((i) => (
                        <InputOTPSlot key={i} index={i} />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <Button className="h-11 w-full rounded-lg font-semibold" onClick={handleVerifyOTP} disabled={loading || otp.length !== 6}>
                  Verify OTP
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </AuthShell>
  );
};

export default BRMLogin;
